/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */

import React from 'react'

import { EuiContextMenuItem, EuiIcon, EuiToolTip } from '@elastic/eui'

import { FormattedMessage } from '@modules/i18n'
import type { AnyAdminProject } from '@modules/project-admin-api/types'
import { useRestoreProjectMutation } from '@modules/project-admin-lib/hooks/restore'
import { addToast } from '@modules/cui/Toasts'
import usePermissions from '@modules/permissions-lib/hooks'
import { useProjectsMetadata } from '@modules/project-admin-components/SupplyProjectsMetadata'
import { MAX_PROJECTS_PER_TRIAL_ORGANIZATION } from '@modules/project-lib/consts'
import ProjectApiErrorCallout from '@modules/project-components/ProjectApiErrorCallout'
import useGetBillingDetails from '@modules/billing-lib/queries/useGetBillingDetails'
import { isTrial } from '@modules/billing-lib/billingDetails/utils'

export type Props = {
  project: AnyAdminProject
  onComplete: () => void
}

const RestoreProjectButton: React.FunctionComponent<Props> = ({ project, onComplete }) => {
  const { hasPermissions: hasRestoreProjectPermissions } = usePermissions([
    {
      action: 'restore',
      type: `project-${project.type}`,
      id: project.id,
    },
  ])

  const restoreProjectMutation = useRestoreProjectMutation(project.type)
  const { projectsLength, isReady } = useProjectsMetadata({
    organizationId: project.metadata.organization_id,
  })

  const { data: billingDetails, isLoading: billingDetailsLoading } = useGetBillingDetails(
    { pathParameters: { organization_id: project.metadata.organization_id } },
    { enabled: Boolean(project.metadata.organization_id) },
  )

  const isTrialOrganization = billingDetails ? isTrial(billingDetails) : false
  const hasMaxNumberOfActiveProjects =
    isTrialOrganization && projectsLength >= MAX_PROJECTS_PER_TRIAL_ORGANIZATION

  const isLoading = !isReady || billingDetailsLoading

  const action = (
    <EuiContextMenuItem
      disabled={!hasRestoreProjectPermissions || isLoading || hasMaxNumberOfActiveProjects}
      icon={<EuiIcon type='refresh' size='m' />}
      onClick={() => {
        restoreProjectMutation.mutate({ id: project.id }, { onSuccess, onError })
      }}
    >
      <FormattedMessage id='project.restoreButton' defaultMessage='Restore project' />
    </EuiContextMenuItem>
  )

  if (!isLoading && hasMaxNumberOfActiveProjects) {
    return (
      <EuiToolTip
        content={
          <FormattedMessage
            id='project.restoreButton.maxProjectsTooltip'
            defaultMessage='This organization has reached the maximum number of projects during a trial'
          />
        }
      >
        {action}
      </EuiToolTip>
    )
  }

  return action

  function onSuccess() {
    addToast({
      family: 'project.restore',
      color: 'success',
      title: <FormattedMessage id='project.restore.success' defaultMessage='Project restored' />,
    })
    onComplete()
  }

  function onError(error) {
    addToast({
      family: 'project.restore',
      color: 'danger',
      title: (
        <FormattedMessage id='project.restore.error' defaultMessage='Failed to restore project' />
      ),
      text: <ProjectApiErrorCallout error={error} />,
    })
  }
}

export default RestoreProjectButton
